import {
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type Ref,
  useMemo,
} from "react";

import {
  IconCheck,
  IconExclamation,
  IconInfo,
  IconMinus,
  IconX,
} from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

export type TBuiStatusBadgeDisplayTypes =
  | "default"
  | "info"
  | "warning"
  | "danger"
  | "success";

/**
 * Props for the BuiStatusBadge presentational primitive.
 */
export interface IBuiStatusBadgeProps {
  /**
   * The id attribute for the badge container.
   * Ids need to start with a letter and can only contain letters, numbers, and dashes.
   */
  id?: string;

  /**
   * The data-testid so it can be granular for testing.
   */
  testid?: string;

  /**
   * The classname applied to the inner badge container (the colored pill).
   */
  className?: string;

  /**
   * The inline styles to be added to the outer dom element.
   */
  style?: CSSProperties;

  /**
   * A text label describing the badge for assistive technologies.
   * @default undefined
   */
  ariaLabel?: string;

  /**
   * Specifies the style or type of the badge, which controls its color and default icon.
   * @default "default"
   */
  displayType?: TBuiStatusBadgeDisplayTypes;

  /**
   * The icon to be displayed within the badge.
   * If not provided, a default icon corresponding to the `displayType` will be used.
   * @default undefined
   */
  icon?: ReactNode;

  /**
   * The text content to be displayed in the badge.
   * @default undefined
   */
  text?: string;

  /**
   * When `true`, the badge displays text content only, hiding any associated icon.
   * @default false
   */
  textOnly?: boolean;

  /**
   * Ref to the inner text node. Consumers use it to measure the rendered text for
   * truncation detection. The badge itself does not read it.
   * @default undefined
   */
  textRef?: Ref<HTMLDivElement>;
}

/**
 * Maps each display type to its colored-pill tailwind utilities, using the semantic
 * foreground/surface design tokens (not raw palette colors).
 */
const displayTypeClassNames: Record<TBuiStatusBadgeDisplayTypes, string> = {
  default: "text-fg-secondary bg-surface-neutral-subtle",
  info: "text-fg-info bg-surface-info-subtle",
  warning: "text-fg-warning bg-surface-warning-subtle",
  danger: "text-fg-error bg-surface-error-subtle",
  success: "text-fg-success bg-surface-success-subtle",
};

const getDefaultIconByDisplayType = (
  testid?: string,
): Record<TBuiStatusBadgeDisplayTypes, ReactNode> => ({
  default: (
    <IconMinus
      testid={`${testid}-icon-Minus`}
      size="md"
      ariaLabel="Minus"
      className="text-fg-secondary"
    />
  ),
  info: (
    <IconInfo
      testid={`${testid}-icon-Info`}
      size="md"
      ariaLabel="Info"
      className="text-fg-info"
    />
  ),
  warning: (
    <IconExclamation
      testid={`${testid}-icon-Exclamation`}
      size="md"
      ariaLabel="Exclamation"
      className="text-fg-warning"
    />
  ),
  danger: (
    <IconX
      testid={`${testid}-icon-X`}
      size="md"
      ariaLabel="Danger"
      className="text-fg-error"
    />
  ),
  success: (
    <IconCheck
      testid={`${testid}-icon-Check`}
      size="md"
      ariaLabel="Check"
      className="text-fg-success"
    />
  ),
});

/**
 * BuiStatusBadge - a presentational status badge primitive.
 *
 * Renders a colored pill containing an icon and/or text. The color and default icon are
 * driven by `displayType`. This is a "dumb" primitive: it has no truncation detection and
 * no tooltip — those live in the app-facing `BTStatusBadge` wrapper. Extend the pill with
 * tailwind utility classes via `className`.
 *
 * @param ref - ref to the outer container element.
 */
export const BuiStatusBadge = forwardRef<HTMLDivElement, IBuiStatusBadgeProps>(
  function BuiStatusBadge(
    {
      id,
      displayType = "default",
      text,
      textOnly,
      className,
      ariaLabel,
      testid,
      style,
      icon,
      textRef,
    },
    ref,
  ) {
    const resolvedIcon = useMemo(
      () => icon ?? getDefaultIconByDisplayType(testid)[displayType],
      [icon, testid, displayType],
    );

    return (
      <div
        id={id}
        ref={ref}
        className="min-w-6 min-h-6"
        data-testid={testid}
        style={style}
      >
        <div
          className={cn(
            "flex max-w-max items-center gap-1 overflow-hidden rounded-lg px-2 py-0.5 font-sans text-sm font-normal whitespace-nowrap",
            { "p-0.5": !text },
            displayTypeClassNames[displayType],
            className,
          )}
          aria-label={ariaLabel}
          // role="status" gives the generic <div> a role that supports aria-label, so screen
          // readers don't ignore it. Only applied when an ariaLabel is provided.
          role={ariaLabel ? "status" : undefined}
          data-testid={`${testid}--container`}
        >
          {!textOnly && <div className="flex">{resolvedIcon}</div>}

          {text && (
            <div ref={textRef} className="overflow-hidden text-ellipsis">
              {text}
            </div>
          )}
        </div>
      </div>
    );
  },
);

BuiStatusBadge.displayName = "BuiStatusBadge";
